/**
 * MultiSlideBuilder Agent - System Prompt Assembly
 * 
 * Combines all sections into the complete system prompt.
 */

import { SystemPromptBuilder } from '../../../base/systemPrompt';
import type { ToolBase } from '../../../base/toolBase';
import { getIntroSection, MULTI_SLIDE_BUILDER_INTRO } from './intro';
import { buildToolsSection } from './tools';
import { getSuggestionsSection, MULTI_SLIDE_BUILDER_SUGGESTIONS } from './suggestions';
import { getContextNoteSection, MULTI_SLIDE_BUILDER_CONTEXT_NOTE } from './contextNote';
import { getEvaluationSection, MULTI_SLIDE_BUILDER_EVALUATION } from './evaluation';

// Import shared builder prompt content
import {
    DENSITY_SECTION_FULL,
    STYLE_GUIDE_PHILOSOPHY,
    SEARCH_AGENT_GUIDANCE,
    AREA_DEFINITION_BEST_PRACTICES,
    generateAvailableSlidesSection,
} from '../../prompts';

export interface MultiSlideBuilderPromptParams {
    planTitle: string;
    slideCount: number;
    sectionCount: number;
    sections: Array<{ name: string; slides: Array<{ name: string; slidePurpose: string }> }>;
    planId: string;
    styleGuidedocumentId: string;
    styleGuideSlideCount: number;
    workingPresentationPath: string;
    workingWorkbookPath: string;
    slideDimensions: { width: number; height: number };
    styleGuideOverview?: {
        slideDescriptions: Array<{
            slide_index?: number;
            layout_name?: string;
            slide_type: string;
            description?: string;
        }>;
        layoutDescriptions: Array<{
            layout_id: string;
            master_name?: string;
            description?: string;
        }>;
        topStyleSignatures: Array<{
            style?: Record<string, unknown>;
            usage_percentage?: number;
        }>;
        layoutDistribution: Record<string, number>;
        slideTypeDistribution?: Record<string, number>;
    };
    tools: ToolBase[];
}

export function buildMultiSlideBuilderSystemPrompt(params: MultiSlideBuilderPromptParams): string {
    // Build the full plan text
    const fullPlanText = buildFullPlanText(params);
    
    // Build style guide overview
    const styleGuideOverviewStr = buildStyleGuideOverviewSection(params);
    
    // Build available slides section
    const availableSlidesSection = generateAvailableSlidesSection({
        styleGuideSlideCount: params.styleGuideSlideCount,
        workingSlideCount: params.slideCount,
    });
    
    // Build sections list
    const sectionList = params.sections.map((s, i) => 
        `  ${i + 1}. "${s.name}" (${s.slides.length} slides)`
    ).join('\n');
    
    // Assemble the prompt using SystemPromptBuilder
    const sections: Parameters<typeof SystemPromptBuilder.build>[0]['sections'] = {
        intro: getIntroSection() + `

**Current Build:**
- Plan: "${params.planTitle}"
- ${params.sectionCount} sections, ${params.slideCount} slides total

---

${availableSlidesSection}

---

## Presentation Plan

Full plan structure:
${params.sections.map((s, i) => `${i + 1}. ${s.name} (${s.slides.length} slides)\n${s.slides.map((sl, j) => `   ${j + 1}. ${sl.name}: ${sl.slidePurpose}`).join('\n')}`).join('\n\n')}

**Use this to:**
- Understand how each slide fits into the narrative flow
- Avoid repeating content covered in earlier sections
- Don't preempt content that will be covered later
- Build coherent transitions between sections

---
${styleGuideOverviewStr}
---

${DENSITY_SECTION_FULL}

---

${STYLE_GUIDE_PHILOSOPHY}

${SEARCH_AGENT_GUIDANCE}

---

## Respecting Layout and Master

The style guide slides contain master/layout information that defines:
- **Content bounds**: Where content CAN go (the safe zone)
- **Exclusion zones**: Where content CANNOT go (logos, footers, page numbers)
- **Visual anchors**: Elements that provide structure (headers, dividers)

When defining areas:
- Place areas WITHIN the content bounds from search results
- NEVER overlap with exclusion zones
- Align areas similarly to how the reference slide uses space
- If reference has 60% left / 40% right split, follow that pattern
- Match the density level of the reference (if it's packed, yours should be too)

---

${AREA_DEFINITION_BEST_PRACTICES}

---

## Build Overview

- Plan ID: ${params.planId}
- Style Guide Deck ID: ${params.styleGuidedocumentId}
- Sections: ${params.sectionCount}
- Total Slides: ${params.slideCount}

## Reference Dimensions

- Slide size: ${params.slideDimensions.width} x ${params.slideDimensions.height} points
- All regions use normalized coordinates (0.0 - 1.0) that scale to actual points

## Sections to Build
${sectionList}

## Working Files

- Presentation: ${params.workingPresentationPath}
- Workbook: ${params.workingWorkbookPath}

---

## Build Process

For EACH section in order:

### Step 1: Reference Search (CRITICAL)

Use presentationLibrarySearchAgent to find relevant reference slides from the style guide:
\`\`\`json
{
  "query": "relevant topics for this section",
  "documentIds": ["${params.styleGuidedocumentId}"],
  "maxResults": 15
}
\`\`\`

### Step 2: Content Research (if needed)

If the section needs data/statistics, use:
- alphaSenseSearchAgent: For financial data, earnings, market research
- externalFileSearchAgent: For uploaded documents
- imageSearchAgent: For logos (brandfetch) or stock images (wikimedia)

Research results return context IDs (as_xxx, logo_xxx, etc.) to pass to executeSection.

### Step 3: Excel Data (if needed)

If the section needs charts/tables:
- Use linkedExcelEditorAgent to populate the working workbook
- Create ranges and charts that can be linked to slides
- Excel results return excelread_xxx IDs and artifact_xxx for chart images

### Step 4: Define Areas for Each Slide

Based on the slide purpose and layout constraints from search results, define areas following the guidelines above.

### Step 5: Execute Section

Call executeSection with:
\`\`\`json
{
  "planId": "${params.planId}",
  "sectionId": "section_id",
  "sectionTitle": "Section Title",
  "overallPlan": ${JSON.stringify(fullPlanText)},
  "globalContext": [
    { "context": ["logo_xxx"], "purpose": "Company logo for all slides" }
  ],
  "referenceSlideIndices": [1, 3, 6],
  "slides": [
    {
      "sourceSlideIndex": 2,
      "position": 0,
      "title": "Slide Title",
      "purpose": "What this slide should communicate",
      "areas": [
        { 
          "areaId": "title", 
          "region": { "x": 0.05, "y": 0.05, "width": 0.9, "height": 0.1 }, 
          "purpose": "Slide title text", 
          "contentType": "text" 
        },
        { 
          "areaId": "main_content", 
          "region": { "x": 0.05, "y": 0.18, "width": 0.9, "height": 0.7 }, 
          "purpose": "Main body content", 
          "contentType": "mixed" 
        }
      ],
      "context": [
        { "context": ["as_001", "as_002"], "purpose": "Research findings for this slide" }
      ]
    }
  ],
  "excelContext": {
    "workbookPath": "${params.workingWorkbookPath}",
    "ranges": [...],
    "purpose": "Data for charts"
  }
}
\`\`\`

**Key fields:**
- \`sourceSlideIndex\`: Style guide slide to copy (from search results)
- \`layoutId\` + \`documentId\`: PREFERRED - uses pre-processed layout-only PPTX
- \`position\`: Where to insert in the presentation (0-based)
- \`areas\`: YOU define these based on slide purpose + layout constraints
- \`context\`: Per-slide context (research, images specific to this slide)
- \`globalContext\`: Context for ALL slides (shared branding, etc.)
- \`overallPlan\`: Full plan text so ExecutionAgent understands context

---

## Error Recovery

If executeSection returns success: false:
1. The section was automatically rolled back to its "before" checkpoint
2. You may retry the section ONCE with different research or approach
3. If the retry also fails, SKIP the section and move on to the next one
4. Log skipped sections in your final summary

DO NOT retry a section more than once - move on to keep the build progressing.

---

## Completion

After ALL sections are attempted (built or skipped):
- Call submitResults with a summary
- Include total sections built vs skipped
- Note which sections failed and why

START with section 1: "${params.sections[0]?.name || 'First section'}"`,
        tools: buildToolsSection(params.tools),
        suggestions: getSuggestionsSection(),
        contextNote: getContextNoteSection(),
        evaluation: getEvaluationSection(),
    };
    
    return SystemPromptBuilder.build({
        sections,
        includeSectionHeaders: false,
        separator: '\n\n',
    });
}

/**
 * Build full plan text for passing to ExecutionAgent
 */
function buildFullPlanText(params: MultiSlideBuilderPromptParams): string {
    const lines: string[] = [];
    lines.push(`Presentation: ${params.planTitle}`);
    lines.push(`Total Slides: ${params.slideCount}`);
    lines.push('');
    
    let slideNum = 1;
    for (const section of params.sections) {
        lines.push(`## ${section.name}`);
        for (const slide of section.slides) {
            lines.push(`  ${slideNum}. ${slide.name}: ${slide.slidePurpose}`);
            slideNum++;
        }
        lines.push('');
    }
    
    return lines.join('\n');
}

// Variables and prompt configuration
export {
    MULTI_SLIDE_BUILDER_VARIABLES,
    MULTI_SLIDE_BUILDER_PROMPT_KEYS,
    MULTI_SLIDE_BUILDER_PROMPT_CONFIG,
} from './variables';

/**
 * Build the style guide overview section for the prompt
 */
function buildStyleGuideOverviewSection(params: MultiSlideBuilderPromptParams): string {
    const overview = params.styleGuideOverview;
    if (!overview) {
        return `
## Style Guide Presentation Overview

No style guide overview available. Use presentationLibrarySearchAgent to discover the style guide structure.
`;
    }
    
    // Build slide descriptions section
    const slideDescStr = overview.slideDescriptions.length > 0
        ? overview.slideDescriptions.map(s => 
            `- Slide ${s.slide_index ?? '?'} [${s.layout_name || 'Unknown Layout'}] (${s.slide_type}): ${s.description || 'No description'}`
        ).join('\n')
        : 'No slide descriptions available.';
    
    // Build layout descriptions section
    const layoutDescStr = overview.layoutDescriptions.length > 0
        ? overview.layoutDescriptions.map(l => 
            `- **${l.layout_id}** (${l.master_name || 'Unknown Master'}): ${l.description || 'No description'}`
        ).join('\n')
        : 'No layout descriptions available.';
    
    // Build top style signatures section
    const topStylesStr = overview.topStyleSignatures.length > 0
        ? overview.topStyleSignatures.slice(0, 5).map(s => {
            const style = s.style || {};
            const fontFamily = (style.font_family as string) || 'Unknown';
            const fontSize = style.font_size ? `${style.font_size}pt` : '?pt';
            const fontColor = (style.font_color as string) || '';
            return `- ${fontFamily} ${fontSize} ${fontColor} (${s.usage_percentage}% of shapes)`;
        }).join('\n')
        : 'No style patterns available.';
    
    // Build layout distribution section
    const layoutDistStr = Object.keys(overview.layoutDistribution).length > 0
        ? Object.entries(overview.layoutDistribution)
            .sort(([, a], [, b]) => b - a)
            .map(([name, count]) => `- ${name}: ${count} slides`)
            .join('\n')
        : 'No layout distribution available.';
    
    // Build slide type distribution section  
    const typeDistStr = Object.keys(overview.slideTypeDistribution || {}).length > 0
        ? Object.entries(overview.slideTypeDistribution || {})
            .sort(([, a], [, b]) => b - a)
            .map(([type, count]) => `- ${type}: ${count} slides`)
            .join('\n')
        : '';
    
    return `
## Style Guide Presentation Overview

The style guide deck has ${overview.slideDescriptions.length} slides. Here is a summary:

### Slide Descriptions (for reference when searching)
${slideDescStr}

### Available Layouts
${layoutDescStr}

### Top Style Patterns (use these consistently)
${topStylesStr}

### Layout Distribution
${layoutDistStr}
${typeDistStr ? `\n### Slide Type Distribution\n${typeDistStr}` : ''}

**Use this overview to:**
- Understand what reference slides are available before searching
- Know which layouts exist and what they're for
- Apply consistent styling that matches the deck's patterns
`;
}
